import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'

export interface BrandSubmission {
  id: string
  userId: string
  name: string
  country?: string
  description?: string
  website?: string
  status: 'pending' | 'approved' | 'rejected'
  adminNotes?: string
  createdAt: string
  updatedAt: string
}

export interface CigarLineSubmission {
  id: string
  userId: string
  brandId: string
  name: string
  description?: string
  strengthLevel?: string
  wrapperType?: string
  binderType?: string
  fillerType?: string
  status: 'pending' | 'approved' | 'rejected'
  adminNotes?: string
  createdAt: string
  updatedAt: string
  brand: {
    id: string
    name: string
  }
}

export interface CreateBrandSubmissionRequest {
  name: string
  country?: string
  description?: string
  website?: string
}

export interface CreateCigarLineSubmissionRequest {
  brandId: string
  name: string
  description?: string
  strengthLevel?: string
  wrapperType?: string
  binderType?: string
  fillerType?: string
}

export interface CigarSubmission {
  id: string
  userId: string
  name: string
  brand: string
  vitola: string
  wrapperType: string
  originCountry: string
  description?: string
  strengthLevel?: string
  notes?: string
  status: 'pending' | 'approved' | 'rejected' | 'modification_requested'
  adminNotes?: string
  reviewedBy?: string
  reviewedAt?: string
  approvedCigarId?: string
  createdAt: string
  updatedAt: string
  user?: {
    id: string
    username: string
    email: string
  }
}

export interface CreateCigarSubmissionRequest {
  name: string
  brand: string
  vitola: string
  wrapperType: string
  originCountry: string
  description?: string
  strengthLevel?: string
  notes?: string
}

export interface ReviewCigarSubmissionRequest {
  action: 'approve' | 'reject' | 'request_modification'
  adminNotes?: string
}

export interface PaginatedSubmissionsResponse {
  success: boolean
  data: CigarSubmission[]
  pagination: {
    page: number
    limit: number
    totalCount: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
}

export interface SubmissionApiResponse<T = unknown> {
  success: boolean
  message: string
  data: T
}

export const submissionApi = createApi({
  reducerPath: 'submissionApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/submissions',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['BrandSubmission', 'CigarLineSubmission', 'CigarSubmission'],
  endpoints: (builder) => ({
    // Get user's brand submissions
    getBrandSubmissions: builder.query<BrandSubmission[], void>({
      query: () => '/brands',
      transformResponse: (response: SubmissionApiResponse<BrandSubmission[]>) => response.data,
      providesTags: ['BrandSubmission'],
    }),

    // Get user's cigar line submissions
    getCigarLineSubmissions: builder.query<CigarLineSubmission[], void>({
      query: () => '/cigar-lines',
      transformResponse: (response: SubmissionApiResponse<CigarLineSubmission[]>) => response.data,
      providesTags: ['CigarLineSubmission'],
    }),

    // Submit new brand
    submitBrand: builder.mutation<BrandSubmission, CreateBrandSubmissionRequest>({
      query: (submission) => ({
        url: '/brands',
        method: 'POST',
        body: submission,
      }),
      transformResponse: (response: SubmissionApiResponse<BrandSubmission>) => response.data,
      invalidatesTags: ['BrandSubmission'],
    }),

    // Submit new cigar line
    submitCigarLine: builder.mutation<CigarLineSubmission, CreateCigarLineSubmissionRequest>({
      query: (submission) => ({
        url: '/cigar-lines',
        method: 'POST',
        body: submission,
      }),
      transformResponse: (response: SubmissionApiResponse<CigarLineSubmission>) => response.data,
      invalidatesTags: ['CigarLineSubmission'],
    }),

    // Get submission by ID
    getBrandSubmission: builder.query<BrandSubmission, string>({
      query: (id) => `/brands/${id}`,
      transformResponse: (response: SubmissionApiResponse<BrandSubmission>) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'BrandSubmission', id }],
    }),

    // Get cigar line submission by ID
    getCigarLineSubmission: builder.query<CigarLineSubmission, string>({
      query: (id) => `/cigar-lines/${id}`,
      transformResponse: (response: SubmissionApiResponse<CigarLineSubmission>) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'CigarLineSubmission', id }],
    }),

    // --- Cigar Submission endpoints ---

    // Get authenticated user's cigar submissions
    getCigarSubmissions: builder.query<CigarSubmission[], void>({
      query: () => '/cigars',
      transformResponse: (response: SubmissionApiResponse<CigarSubmission[]>) => response.data,
      providesTags: ['CigarSubmission'],
    }),

    // Submit a new cigar
    submitCigar: builder.mutation<CigarSubmission, CreateCigarSubmissionRequest>({
      query: (submission) => ({
        url: '/cigars',
        method: 'POST',
        body: submission,
      }),
      transformResponse: (response: SubmissionApiResponse<CigarSubmission>) => response.data,
      invalidatesTags: ['CigarSubmission'],
    }),

    // Get a single cigar submission by ID
    getCigarSubmission: builder.query<CigarSubmission, string>({
      query: (id) => `/cigars/${id}`,
      transformResponse: (response: SubmissionApiResponse<CigarSubmission>) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'CigarSubmission', id }],
    }),

    // Admin: Get pending submissions
    getPendingCigarSubmissions: builder.query<PaginatedSubmissionsResponse, { page?: number; limit?: number }>({
      query: ({ page = 1, limit = 20 } = {}) => `/cigars/admin/pending?page=${page}&limit=${limit}`,
      providesTags: ['CigarSubmission'],
    }),

    // Admin: Get all submissions with optional status filter
    getAllCigarSubmissions: builder.query<PaginatedSubmissionsResponse, { page?: number; limit?: number; status?: string }>({
      query: ({ page = 1, limit = 20, status } = {}) =>
        `/cigars/admin/all?page=${page}&limit=${limit}${status ? `&status=${status}` : ''}`,
      providesTags: ['CigarSubmission'],
    }),

    // Admin: Review a submission
    reviewCigarSubmission: builder.mutation<CigarSubmission, { id: string } & ReviewCigarSubmissionRequest>({
      query: ({ id, ...body }) => ({
        url: `/cigars/${id}/review`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: SubmissionApiResponse<CigarSubmission>) => response.data,
      invalidatesTags: ['CigarSubmission'],
    }),
  }),
})

export const {
  useGetBrandSubmissionsQuery,
  useGetCigarLineSubmissionsQuery,
  useSubmitBrandMutation,
  useSubmitCigarLineMutation,
  useGetBrandSubmissionQuery,
  useGetCigarLineSubmissionQuery,
  useGetCigarSubmissionsQuery,
  useSubmitCigarMutation,
  useGetCigarSubmissionQuery,
  useGetPendingCigarSubmissionsQuery,
  useGetAllCigarSubmissionsQuery,
  useReviewCigarSubmissionMutation,
} = submissionApi
